import { useFetch } from "../useFetch";
import { useMemo } from "react";
import type {
	ProviderResponse,
	CreateProviderPayload,
	UpdateProviderPayload,
	BulkCreateProvidersResponse,
} from "../../core/types/control/";

// ============================================
// Tipos Auxiliares para el Hook
// ============================================
type PaginatedResponse<T> = {
	items: T[];
	meta: {
		total: number;
		page: number;
		pageSize: number;
		totalPages: number;
	};
};

type ProviderFilters = {
	page?: number;
	pageSize?: number;
	limit?: number;
	name?: string;
	category?: number;
	subcategory?: number;
	rating?: number;
	responseTime?: number;
	country?: string;
	state?: string;
	city?: string;
	street?: string;
	municipality?: number;
	parish?: number;
	isVerified?: string;
	isActive?: string;
};

// ============================================
// GET ALL PROVIDERS (PAGINATED WITH FILTERS)
// ============================================
// Añadimos un segundo parámetro `auto` (por defecto true)
export function useProviders(filters: ProviderFilters = {}, auto: boolean = true) {
	const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

	// Estabilizamos el objeto query serializándolo para que NO cambie de referencia
	// en cada render si los valores internos son los mismos.
	const query = useMemo(() => {
		const parsedFilters = JSON.parse(filtersKey) as ProviderFilters;
		const { page = 1, pageSize = 10, ...rest } = parsedFilters;
		return { page, pageSize, ...rest };
	}, [filtersKey]);

	return useFetch<PaginatedResponse<ProviderResponse>>({
		endpoint: "/providers",
		method: "GET",
		query,
		auto, // Pasamos la bandera auto al useFetch
	});
}

// ============================================
// GET TOP RATED PROVIDERS
// ============================================
export function useTopRatedProviders() {
	return useFetch<ProviderResponse[]>({
		endpoint: "/providers/top-rated",
		method: "GET",
	});
}

// ============================================
// GET OLDEST PROVIDERS
// ============================================
export function useOldestProviders() {
	return useFetch<ProviderResponse[]>({
		endpoint: "/providers/oldest",
		method: "GET",
	});
}

// ============================================
// GET PROVIDER BY ID
// ============================================
export function useProvider(id: number) {
	return useFetch<ProviderResponse>({
		endpoint: `/providers/${id}`,
		method: "GET",
	});
}

// ============================================
// CREATE PROVIDER
// ============================================
export function useCreateProvider() {
	const fetch = useFetch<ProviderResponse, CreateProviderPayload>({
		endpoint: "/providers",
		method: "POST",
		auto: false,
	});

	const create = (data: CreateProviderPayload) => {
		return fetch.execute({ body: data });
	};

	return { ...fetch, create };
}

// ============================================
// UPDATE PROVIDER
// ============================================
export function useUpdateProvider(id: number) {
	const fetch = useFetch<ProviderResponse, UpdateProviderPayload>({
		endpoint: `/providers/${id}`,
		method: "PATCH",
		auto: false,
	});

	const update = (data: UpdateProviderPayload) => {
		return fetch.execute({ body: data });
	};

	return { ...fetch, update };
}

// ============================================
// UPDATE PROVIDER AVAILABILITY
// ============================================
export function useUpdateProviderAvailability(id: number) {
	const fetch = useFetch<ProviderResponse, { availability: boolean }>({
		endpoint: `/providers/${id}/availability`,
		method: "PATCH",
		auto: false,
	});

	const toggleAvailability = (availability: boolean) => {
		return fetch.execute({ body: { availability } });
	};

	return { ...fetch, toggleAvailability };
}

// ============================================
// BAN/UNBAN PROVIDER
// ============================================
export function useUpdateProviderStatus(id: number) {
	const fetch = useFetch<ProviderResponse, { isBanned: boolean }>({
		endpoint: `/providers/${id}/status`,
		method: "PATCH",
		auto: false,
	});

	const setStatus = (isBanned: boolean) => {
		return fetch.execute({ body: { isBanned } });
	};

	return { ...fetch, setStatus };
}

export function useBulkCreateProviders() {
	const fetch = useFetch<BulkCreateProvidersResponse>({
		endpoint: "/providers/bulk",
		method: "POST",
		auto: false,
	});

	const upload = (file: File) => {
		return fetch.execute({ files: { file } });
	};

	return { ...fetch, upload };
}
